import cn from 'classnames'
import { useEffect, useState } from 'react'

import { useFeedback } from './Feedback'
import styles from './Feedback.module.scss'

export interface ToastOptions {
  message: string
  time?: number
  type?: 'success' | 'error' | 'loading' | 'copy'
}

export const ToastElement = ({
  children,
  id,
  options,
}: {
  options: ToastOptions
  id: string | number
  children: any
}) => {
  const { time, type } = options
  const timeToClose = time ? time : 4000

  const { hideToast } = useFeedback()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      hideToast(id)
    }, timeToClose + 1000)

    const hideTimer = setTimeout(() => {
      setIsOpen(false)
    }, timeToClose)

    return () => {
      clearTimeout(timer)
      clearTimeout(hideTimer)
    }
  }, [id, hideToast])

  useEffect(() => {
    setTimeout(() => {
      setIsOpen(true)
    }, 50)
  }, [])

  return (
    <div className={cn(styles.root)} key={id}>
      <div
        className={cn(
          styles.contentWrapper,
          isOpen && styles.opened,
          styles[`type-${type ? type : 'success'}`]
        )}
      >
        <div className={styles.content}>
          {!type || type === 'success' ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M14 25C20.0751 25 25 20.0751 25 14C25 7.92487 20.0751 3 14 3C7.92487 3 3 7.92487 3 14C3 20.0751 7.92487 25 14 25ZM19.1826 9.91716C19.413 9.54016 19.2942 9.04777 18.9172 8.81737C18.5402 8.58698 18.0478 8.70584 17.8174 9.08284L12.8851 17.1538L10.1021 13.9732C9.81112 13.6407 9.30571 13.607 8.9732 13.8979C8.64069 14.1889 8.60699 14.6943 8.89794 15.0268L12.3979 19.0268C12.5656 19.2184 12.8144 19.3188 13.068 19.2971C13.3217 19.2755 13.5499 19.1344 13.6826 18.9172L19.1826 9.91716Z"
                fill="white"
              />
            </svg>
          ) : null}
          {type === 'loading' ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
            >
              <path
                d="M23.165 14C23.165 12.4319 22.7626 10.89 21.9964 9.52179C21.2302 8.1536 20.1257 7.0049 18.7887 6.18555C17.4516 5.36621 15.9267 4.90363 14.3598 4.84207C12.7929 4.7805 11.2364 5.12201 9.83918 5.83393C8.44196 6.54584 7.25079 7.60435 6.37958 8.9082C5.50838 10.2121 4.98629 11.7176 4.86325 13.2809C4.74022 14.8442 5.02036 16.4129 5.67687 17.837C6.33338 19.2611 7.34431 20.4929 8.61295 21.4146"
                stroke="white"
                strokeWidth="2.33"
                strokeLinecap="round"
              />
            </svg>
          ) : null}
          {type === 'error' ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
            >
              <path
                d="M6 6L22 22M6 22L22 6"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          ) : null}
          {type === 'copy' ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M14.0001 3.8H14.9999C16.2548 3.8 17.0876 3.8014 17.7269 3.85363C18.3451 3.90414 18.6221 3.99308 18.794 4.08067C19.2785 4.32755 19.6725 4.72148 19.9194 5.20601C20.007 5.37791 20.0959 5.65491 20.1464 6.27315C20.1986 6.91241 20.2 7.74528 20.2 9.00011V9.99997C20.2 11.2548 20.1986 12.0877 20.1464 12.7269C20.0959 13.3452 20.007 13.6222 19.9194 13.7941C19.6725 14.2786 19.2785 14.6725 18.794 14.9194C18.6221 15.007 18.3451 15.0959 17.7269 15.1465C17.5099 15.1642 17.2705 15.1761 16.9999 15.184C16.9999 15.8781 16.9987 16.4708 16.9849 16.985C18.1954 16.9525 18.971 16.8494 19.6112 16.5232C20.4344 16.1038 21.1037 15.4345 21.5232 14.6112C22 13.6754 22 12.4502 22 9.99997V9.00011C22 6.54984 22 5.32471 21.5232 4.38883C21.1037 3.56561 20.4344 2.89631 19.6112 2.47685C18.6753 2 17.4502 2 14.9999 2H14.0001C11.5498 2 10.3247 2 9.38878 2.47685C8.56556 2.89631 7.89626 3.56561 7.47681 4.38883C7.1506 5.02904 7.04755 5.80461 7.01499 7.01512C7.5292 7.00129 8.12189 7.00018 8.81603 7.00009C8.82398 6.72951 8.83585 6.49017 8.85358 6.27315C8.90409 5.65491 8.99303 5.37791 9.08062 5.20601C9.3275 4.72148 9.72143 4.32755 10.206 4.08067C10.3779 3.99308 10.6549 3.90414 11.2731 3.85363C11.9124 3.8014 12.7452 3.8 14.0001 3.8Z"
                fill="white"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.99997 8.8H9.00012C7.74528 8.8 6.91241 8.8014 6.27315 8.85363C5.65491 8.90414 5.37791 8.99308 5.20601 9.08067C4.72148 9.32755 4.32755 9.72148 4.08067 10.206C3.99308 10.3779 3.90414 10.6549 3.85363 11.2732C3.8014 11.9124 3.8 12.7453 3.8 14.0001V15C3.8 16.2548 3.8014 17.0877 3.85363 17.7269C3.90414 18.3452 3.99308 18.6222 4.08067 18.7941C4.32755 19.2786 4.72148 19.6725 5.20601 19.9194C5.37791 20.007 5.65491 20.0959 6.27315 20.1465C6.9124 20.1987 7.74527 20.2001 9.00011 20.2001H9.99996C11.2548 20.2001 12.0877 20.1987 12.7269 20.1465C13.3452 20.0959 13.6222 20.007 13.7941 19.9194C14.2786 19.6725 14.6725 19.2786 14.9194 18.7941C15.007 18.6222 15.0959 18.3452 15.1465 17.7269C15.1987 17.0877 15.2001 16.2548 15.2001 15V14.0001C15.2001 12.7453 15.1987 11.9124 15.1465 11.2732C15.0959 10.6549 15.007 10.3779 14.9194 10.206C14.6725 9.72148 14.2786 9.32755 13.7941 9.08067C13.6222 8.99308 13.3452 8.90414 12.7269 8.85363C12.0877 8.8014 11.2548 8.8 9.99997 8.8ZM2.47685 9.38883C2 10.3247 2 11.5498 2 14.0001V15C2 17.4502 2 18.6754 2.47685 19.6112C2.89631 20.4345 3.56561 21.1038 4.38883 21.5232C5.32471 22.0001 6.54984 22.0001 9.00011 22.0001H9.99996C12.4502 22.0001 13.6754 22.0001 14.6112 21.5232C15.4345 21.1038 16.1038 20.4345 16.5232 19.6112C17.0001 18.6754 17.0001 17.4502 17.0001 15V14.0001C17.0001 11.5498 17.0001 10.3247 16.5232 9.38883C16.1038 8.56561 15.4345 7.89631 14.6112 7.47685C13.6754 7 12.4502 7 9.99997 7H9.00012C6.54984 7 5.32471 7 4.38883 7.47685C3.56561 7.89631 2.89631 8.56561 2.47685 9.38883Z"
                fill="white"
              />
            </svg>
          ) : null}

          {children}
        </div>
      </div>
    </div>
  )
}
